import { forwardRef, useState, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { Search, X } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: string;
  defaultValue?: string;
  onChange: (value: string) => void;
  debounceMs?: number;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, defaultValue = '', onChange, debounceMs = 300, placeholder = 'Search...', className = '', ...props }, ref) => {
    const [localValue, setLocalValue] = useState(value ?? defaultValue);
    const debouncedValue = useDebounce(localValue, debounceMs);
    const isControlled = value !== undefined;
    const onChangeRef = useRef(onChange);

    // Keep onChange ref up to date
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    // Update local value when controlled value prop changes
    useEffect(() => {
      if (isControlled) {
        setLocalValue(value);
      }
    }, [value, isControlled]);

    // Call onChange when debounced value changes
    useEffect(() => {
      if (isControlled && debouncedValue !== value) {
        onChangeRef.current(debouncedValue);
      } else if (!isControlled) {
        onChangeRef.current(debouncedValue);
      }
    }, [debouncedValue, value, isControlled]);

    const handleClear = () => {
      setLocalValue('');
      onChangeRef.current('');
    };

    return (
      <div className={`relative ${className}`}>
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-foreground-subtle">
          <Search className="h-4 w-4" />
        </div>
        <input
          ref={ref}
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder={placeholder}
          className={`
            block w-full rounded-md border border-border bg-background-subtle text-foreground
            placeholder:text-foreground-subtle
            focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
            disabled:cursor-not-allowed disabled:opacity-50
            h-10 pl-10 pr-10 text-sm
          `}
          {...props}
        />
        {localValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-subtle hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
